/**
 * Date Utility Functions
 *
 * Common date manipulation and calculation functions for leave management
 */

import { DEFAULT_WORKING_DAYS } from "../constants";

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Subtract days from a date
 */
export function subtractDays(date: Date, days: number): Date {
  return addDays(date, -days);
}

/**
 * Check if a date is a weekend
 */
export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday or Saturday
}

/**
 * Check if a date is a working day
 */
export function isWorkingDay(
  date: Date,
  workingDays: number[] = DEFAULT_WORKING_DAYS
): boolean {
  return workingDays.includes(date.getDay());
}

/**
 * Calculate business days between two dates (inclusive)
 */
export function calculateBusinessDays(
  startDate: Date,
  endDate: Date,
  options: {
    workingDays?: number[];
    excludeWeekends?: boolean;
    holidays?: Date[];
  } = {}
): number {
  const {
    workingDays = DEFAULT_WORKING_DAYS,
    excludeWeekends = true,
    holidays = [],
  } = options;

  let count = 0;
  const current = new Date(startDate);
  const end = new Date(endDate);

  // Normalize to start of day for comparison
  current.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  while (current <= end) {
    const isHoliday = holidays.some((holiday) => isSameDay(holiday, current));

    if (!isHoliday) {
      if (excludeWeekends) {
        if (isWorkingDay(current, workingDays)) {
          count++;
        }
      } else {
        count++;
      }
    }

    current.setDate(current.getDate() + 1);
  }

  return count;
}

/**
 * Calculate total days between two dates (inclusive)
 */
export function calculateTotalDays(startDate: Date, endDate: Date): number {
  const start = new Date(startDate);
  const end = new Date(endDate);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays + 1; // +1 to make it inclusive
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Check if a date is today
 */
export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

/**
 * Check if a date is in the past
 */
export function isPast(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
}

/**
 * Check if a date is in the future
 */
export function isFuture(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate > today;
}

/**
 * Get start of day
 */
export function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get end of day
 */
export function endOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Get start of month
 */
export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * Get end of month
 */
export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

/**
 * Get start of year
 */
export function startOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1);
}

/**
 * Get end of year
 */
export function endOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999);
}

/**
 * Format date to ISO string (YYYY-MM-DD)
 */
export function formatDateISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parse ISO date string to Date
 */
export function parseISODate(dateString: string): Date {
  return new Date(dateString);
}

/**
 * Format date for display (e.g., "15 Jan 2024")
 */
export function formatDateDisplay(date: Date): string {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
}

/**
 * Format date for full display (e.g., "15 January 2024")
 */
export function formatDateFull(date: Date): string {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
}

/**
 * Format datetime for display (e.g., "15 Jan 2024 14:30")
 */
export function formatDateTimeDisplay(date: Date): string {
  const dateStr = formatDateDisplay(date);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${dateStr} ${hours}:${minutes}`;
}

/**
 * Get relative time string (e.g., "2 days ago", "in 3 days")
 */
export function getRelativeTimeString(date: Date): string {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1 && diffMinutes > -1) {
    return "just now";
  } else if (diffMinutes < 60 && diffMinutes > 0) {
    return `in ${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""}`;
  } else if (diffMinutes > -60 && diffMinutes < 0) {
    return `${Math.abs(diffMinutes)} minute${Math.abs(diffMinutes) !== 1 ? "s" : ""} ago`;
  } else if (diffHours < 24 && diffHours > 0) {
    return `in ${diffHours} hour${diffHours !== 1 ? "s" : ""}`;
  } else if (diffHours > -24 && diffHours < 0) {
    return `${Math.abs(diffHours)} hour${Math.abs(diffHours) !== 1 ? "s" : ""} ago`;
  } else if (diffDays === 1) {
    return "tomorrow";
  } else if (diffDays === -1) {
    return "yesterday";
  } else if (diffDays > 1 && diffDays < 7) {
    return `in ${diffDays} days`;
  } else if (diffDays < -1 && diffDays > -7) {
    return `${Math.abs(diffDays)} days ago`;
  } else if (diffDays >= 7) {
    return formatDateDisplay(date);
  } else {
    return formatDateDisplay(date);
  }
}

/**
 * Check if date ranges overlap
 */
export function doDateRangesOverlap(
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
): boolean {
  return start1 <= end2 && start2 <= end1;
}

/**
 * Get days in month
 */
export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Get calendar weeks for a month
 */
export function getCalendarWeeks(year: number, month: number): Date[][] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const weeks: Date[][] = [];
  let currentWeek: Date[] = [];

  // Fill in days before the first day of the month
  const firstDayOfWeek = firstDay.getDay();
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const date = new Date(year, month, -i);
    currentWeek.push(date);
  }

  // Fill in the days of the month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day);
    currentWeek.push(date);

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Fill in remaining days after the last day of the month
  if (currentWeek.length > 0) {
    const remainingDays = 7 - currentWeek.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      currentWeek.push(date);
    }
    weeks.push(currentWeek);
  }

  return weeks;
}

/**
 * Calculate age from birth date
 */
export function calculateAge(birthDate: Date): number {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
}

/**
 * Calculate years of service
 */
export function calculateYearsOfService(hireDate: Date): number {
  return calculateAge(hireDate);
}

/**
 * Get fiscal year from date
 * @param date - Date to get fiscal year for
 * @param fiscalYearStartMonth - Month when fiscal year starts (0 = January, 9 = October)
 */
export function getFiscalYear(
  date: Date,
  fiscalYearStartMonth: number = 0
): number {
  const year = date.getFullYear();
  const month = date.getMonth();

  if (month >= fiscalYearStartMonth) {
    return year + 1; // Fiscal year is named after the ending year
  } else {
    return year;
  }
}

/**
 * Thai Fiscal Year starts October 1st and ends September 30th
 * Fiscal year is named after the ending year (e.g., FY 2569 = Oct 1, 2568 - Sep 30, 2569)
 */
export const THAI_FISCAL_YEAR_START_MONTH = 9; // October (0-indexed)

/**
 * Get Thai fiscal year from date
 * Thai fiscal year: October 1 to September 30, named after the ending year
 * Example: Oct 1, 2568 - Sep 30, 2569 = Fiscal Year 2569
 * @param date - Date to get fiscal year for (defaults to current date)
 * @returns Fiscal year in Buddhist Era (พ.ศ.)
 */
export function getThaiFiscalYear(date: Date = new Date()): number {
  const year = date.getFullYear();
  const month = date.getMonth();

  // If month is October (9) or later, fiscal year is next year
  // If month is before October, fiscal year is current year
  const fiscalYearCE = month >= THAI_FISCAL_YEAR_START_MONTH ? year + 1 : year;

  // Convert to Buddhist Era (พ.ศ.)
  return fiscalYearCE + 543;
}

/**
 * Get Thai fiscal year in Christian Era (ค.ศ.)
 * @param date - Date to get fiscal year for (defaults to current date)
 * @returns Fiscal year in Christian Era (ค.ศ.)
 */
export function getThaiFiscalYearCE(date: Date = new Date()): number {
  const year = date.getFullYear();
  const month = date.getMonth();

  return month >= THAI_FISCAL_YEAR_START_MONTH ? year + 1 : year;
}

/**
 * Get fiscal year date range
 * @param fiscalYearBE - Fiscal year in Buddhist Era (e.g., 2569)
 * @returns Object with start and end dates
 */
export function getThaiFiscalYearDateRange(fiscalYearBE: number): {
  startDate: Date;
  endDate: Date;
  startDateString: string;
  endDateString: string;
} {
  const fiscalYearCE = fiscalYearBE - 543;

  // Fiscal year starts October 1 of previous year
  const startDate = new Date(fiscalYearCE - 1, 9, 1); // October 1
  startDate.setHours(0, 0, 0, 0);

  // Fiscal year ends September 30 of the fiscal year
  const endDate = new Date(fiscalYearCE, 8, 30); // September 30
  endDate.setHours(23, 59, 59, 999);

  return {
    startDate,
    endDate,
    startDateString: `1 ตุลาคม ${fiscalYearBE - 1}`,
    endDateString: `30 กันยายน ${fiscalYearBE}`,
  };
}

/**
 * Check if date is within range
 */
export function isDateInRange(
  date: Date,
  startDate: Date,
  endDate: Date
): boolean {
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  return compareDate >= start && compareDate <= end;
}

/**
 * Get next working day
 */
export function getNextWorkingDay(
  date: Date,
  options: {
    workingDays?: number[];
    holidays?: Date[];
  } = {}
): Date {
  const { workingDays = DEFAULT_WORKING_DAYS, holidays = [] } = options;
  let nextDay = addDays(date, 1);

  while (
    !isWorkingDay(nextDay, workingDays) ||
    holidays.some((holiday) => isSameDay(holiday, nextDay))
  ) {
    nextDay = addDays(nextDay, 1);
  }

  return nextDay;
}

/**
 * Get previous working day
 */
export function getPreviousWorkingDay(
  date: Date,
  options: {
    workingDays?: number[];
    holidays?: Date[];
  } = {}
): Date {
  const { workingDays = DEFAULT_WORKING_DAYS, holidays = [] } = options;
  let prevDay = subtractDays(date, 1);

  while (
    !isWorkingDay(prevDay, workingDays) ||
    holidays.some((holiday) => isSameDay(holiday, prevDay))
  ) {
    prevDay = subtractDays(prevDay, 1);
  }

  return prevDay;
}

/**
 * Parse various date formats to Date object
 */
export function parseDate(input: string | number | Date): Date {
  if (input instanceof Date) {
    return input;
  }

  if (typeof input === "number") {
    return new Date(input);
  }

  // Try parsing ISO format first
  const isoDate = new Date(input);
  if (!isNaN(isoDate.getTime())) {
    return isoDate;
  }

  throw new Error(`Invalid date format: ${input}`);
}

/**
 * Validate if a string is a valid date
 */
export function isValidDate(dateString: string): boolean {
  try {
    const date = parseDate(dateString);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}

/**
 * Get month name
 */
export function getMonthName(month: number, full: boolean = false): string {
  const monthsFull = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const monthsShort = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return full ? monthsFull[month]! : monthsShort[month]!;
}

/**
 * Get day name
 */
export function getDayName(day: number, full: boolean = false): string {
  const daysFull = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const daysShort = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return full ? daysFull[day]! : daysShort[day]!;
}
